// Utils and Service
import { getErrorMessage } from '../utils/helpers'
import axios from './instance'

/**
 * Gets the composites for the specified `clientId`.
 * @param {string} url
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` results
 */
export const getClientComposites = async (
  url,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(url)

    setLoading(false)
    setSuccess(true)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Creates a new composite with the specified `payload`.
 * @param {string} clientId
 * @param {object} payload
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 */
export const createClientComposite = async (
  clientId,
  payload,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.post(`/clients/${clientId}/composites/`, payload)
    setSuccess('New composite added.')
    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Updates the composite with the specified `compositeId` and `payload`.
 * @param {string} clientId
 * @param {object} payload
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 * @returns `data` for the new composite
 */
export const updateClientComposite = async (
  clientId,
  payload,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.patch(`/clients/${clientId}/composites/${payload.id}/`, payload)
    setSuccess(`${response.name} updated.`)
    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the count of portfolio data that would be deleted when deleting a composite.
 * @param {string} clientId
 * @param {string} compositeId
 * @param {func} setError
 * @param {func} setLoading
 * @returns count of portfolio data records
 */
export const getCompositePortfolioDataCount = async (
  clientId,
  compositeId,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(`/clients/${clientId}/composites/${compositeId}/portfolio-data-count/`)
    setLoading(false)
    return response.count
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return 0
  }
}

/**
 * Gets the count of benchmark data that would be deleted when deleting a composite.
 * @param {string} clientId
 * @param {string} compositeId
 * @param {func} setError
 * @param {func} setLoading
 * @returns count of benchmark data records
 */
export const getCompositeBenchmarkDataCount = async (
  clientId,
  compositeId,
  setError = () => {},
  setLoading = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(`/clients/${clientId}/composites/${compositeId}/benchmark-data-count/`)
    setLoading(false)
    return response.count
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return 0
  }
}

/**
 * Deletes the composite with the specified `compositeId` and `payload`.
 * @param {string} clientId
 * @param {string} compositeId
 * @param {object} payload
 * @param {func} setError
 * @param {func} setLoading
 * @param {func} setSuccess
 */
export const deleteClientComposite = async (
  clientId,
  compositeId,
  setError = () => {},
  setLoading = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    await axios.delete(`/clients/${clientId}/composites/${compositeId}/`)
    setLoading(false)
    setSuccess()
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
  }
}
